import React, { useCallback, useContext, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { cloneDeep } from "lodash";
import styles from "./CopyAgendaItemPreview.module.css";
import { AgendaItem } from "src/types";
import { toJS } from "mobx";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey, Role } from "src/network/User";
import { GetAgendaitemInformationFromView_agendaitemtemplatetexts } from "src/api/generated/GetAgendaitemInformationFromView";
import { MessageType } from "src/components/notifications/Notifier";
import Axios from "src/network/Axios";

interface CopyAssemblyAgendaModalProps {
    assemblyid: string;
    agendaitemTemplatesCounter: number;
    agendaItemTemplates: GetAgendaitemInformationFromView_agendaitemtemplatetexts[] | undefined;
    isAssemblyLoaded: boolean;
}

interface CopyUrlParams {
    selectedCopyAgendaOption: number;
    dropdownOption: number;
    assemblyId: string | number;
    selectedAssemblyId: string | number;
}

const managerHeaders = {
    headers: {
        "x-hasura-role": getRoleKey(Role.MANAGER)
    }
};

const buildCopyUrl = ({
    selectedCopyAgendaOption,
    dropdownOption,
    assemblyId,
    selectedAssemblyId
}: CopyUrlParams): { url: string; baseUrl: string } => {
    if (selectedCopyAgendaOption === 1) {
        const baseUrl =
            dropdownOption === 1 ? NetworkConfig.newCopyagendaitems : NetworkConfig.newCopyagendaitemsWithText;
        return { url: `${baseUrl}?newassemblyid=${assemblyId}&oldassemblyid=${selectedAssemblyId}`, baseUrl };
    }

    const baseUrl = NetworkConfig.newCopyagendaitemTemplates;
    return { url: `${baseUrl}?newassemblyid=${assemblyId}`, baseUrl };
};

const buildSignaturesUrl = (assemblyId: string | number, selectedAssemblyId: string | number) =>
    `${NetworkConfig.copySignaturesOnly}?newassemblyid=${assemblyId}&oldassemblyid=${selectedAssemblyId}`;

const CopyAgendaItemPreviewBase = (props: CopyAssemblyAgendaModalProps) => {
    const { t } = useTranslation();
    const { assemblyStore, authStore, uiStore, agendaitemStore } = useContext(RootStoreContext);
    const { assemblyid, agendaitemTemplatesCounter, agendaItemTemplates, isAssemblyLoaded } = props;

    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        agendaitemStore.updateNavigation(assemblyStore.currentAssemblyTitle);

        if (isAssemblyLoaded) {
            agendaitemStore.loadAgendaItemsToCopyFromAssembly(assemblyid);
        }
    }, [agendaitemStore, assemblyid, isAssemblyLoaded, assemblyStore.currentAssemblyTitle]);

    const printStatusMessage = useCallback(
        (message: string, msgType: MessageType) => {
            uiStore.enqueueSnackbar({
                content: message,
                options: {
                    type: msgType,
                    autoClose: msgType === MessageType.ERROR ? false : 3000
                }
            });
        },
        [uiStore]
    );

    const handleDropdownOptionClick = useCallback(
        (dropdownOption: number) => async (e: React.MouseEvent<HTMLLIElement | HTMLButtonElement>) => {
            e.preventDefault();

            if (assemblyStore.selectedCopyAssemblyAgendaOption <= 0) return;
            if (!authStore.user || !authStore.token) return;

            if (assemblyStore.agendaItemsHaveChanged) {
                const agendaItems = cloneDeep(toJS(assemblyStore.agendaItems)) as AgendaItem[];
                const unsavedAgendaItems = agendaItems.filter((agi) => agi.isNew);
                if (unsavedAgendaItems.length > 0) {
                    assemblyStore.setUnsavedAgendaItems(unsavedAgendaItems);
                }
            }

            const { url: copyUrl, baseUrl } = buildCopyUrl({
                selectedCopyAgendaOption: assemblyStore.selectedCopyAssemblyAgendaOption,
                dropdownOption,
                assemblyId: assemblyid,
                selectedAssemblyId: agendaitemStore.selectedAssembly
            });

            try {
                await Axios.getAuthInstance().get(copyUrl, { ...managerHeaders, urlWithoutParams: baseUrl });

                if (assemblyStore.selectedCopyAssemblyAgendaOption === 1) {
                    const signaturesUrl = buildSignaturesUrl(assemblyid, agendaitemStore.selectedAssembly);
                    try {
                        await Axios.getAuthInstance().get(signaturesUrl, {
                            ...managerHeaders,
                            urlWithoutParams: NetworkConfig.copySignaturesOnly
                        });
                    } catch (error) {
                        console.error("Error while copying signatures:", error);
                    }
                    printStatusMessage(
                        t("screens.realestate.assembly.copy_agendaitem_modal.agendacopymessage"),
                        MessageType.INFO
                    );
                } else {
                    printStatusMessage(
                        t("screens.realestate.assembly.copy_agendaitem_modal.agendatemplatecopymessage"),
                        MessageType.INFO
                    );
                }

                assemblyStore.setIsCopyAgendaItemsActive(true);
                window.location.reload();
            } catch (error) {
                console.error("Error while copying agenda items:", error);
            }
        },
        [
            authStore.token,
            authStore.user,
            assemblyid,
            t,
            assemblyStore,
            agendaitemStore.selectedAssembly,
            printStatusMessage
        ]
    );

    const onRadioOptionChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = event.currentTarget;
            if (value === "AGENDAITEMS_LAST_ASSEMBLY") {
                assemblyStore.setSelectedCopyAssemblyAgendaOption(1);
            } else if (value === "AGENDAITEMS_TEMPLATE_ASSEMBLY") {
                assemblyStore.setSelectedCopyAssemblyAgendaOption(2);
            } else {
                assemblyStore.setSelectedCopyAssemblyAgendaOption(0);
            }
        },
        [assemblyStore]
    );

    const onSelectDropdown = useCallback(
        async (realestateId: string) => {
            const selected = assemblyStore.formattedRealestates.find((realestate) => realestate.id === realestateId);
            if (!selected) return;

            agendaitemStore.setSelectedRealestate(selected);
            await agendaitemStore.loadAgendaItemsToCopy(
                selected.id,
                assemblyid,
                assemblyStore.currentAssembly?.date || ""
            );
            agendaitemStore.setSelectedAssembly(
                agendaitemStore.currentAgendaItemToCopy?.length > 0
                    ? agendaitemStore.currentAgendaItemToCopy[0].assemblyid
                    : ""
            );
            assemblyStore.setSelectedCopyAssemblyAgendaOption(1);
        },
        [assemblyStore, agendaitemStore, assemblyid]
    );

    const agendaItemsExists =
        assemblyStore.selectedCopyAssemblyAgendaOption !== 1 ||
        (agendaitemStore.currentAgendaItemToCopy?.length ?? 0) > 0;

    return (
        <UI.Container className="pt-4">
            <UI.Row>
                <UI.Col xs={10} sm={10} md={10}>
                    <UI.Title>{t("screens.assemblies.agendas")}</UI.Title>
                </UI.Col>
                <UI.Col xs={2} sm={2} md={2}>
                    {agendaItemsExists && (
                        <div className={styles.ApplyButtonContainer}>
                            <div ref={wrapperRef} className={styles.HeaderButtons}>
                                <UI.Button
                                    variant="outline-primary"
                                    className={uiStore.isMobile ? styles.ApplyButtonMobile : styles.ApplyButton}
                                    onClick={() => agendaitemStore.toggleDropdown()}
                                    label={
                                        <div className={styles.ButtonContent}>
                                            {!uiStore.isMobile && <span>{t("labels.apply")}</span>}
                                            <UI.Icon
                                                icon={
                                                    agendaitemStore.isDropdownOpen
                                                        ? UI.SVGIcon.ChevronUp
                                                        : UI.SVGIcon.ChevronDown
                                                }
                                                size={uiStore.isMobile ? 30 : 17}
                                            />
                                        </div>
                                    }
                                />
                                {agendaitemStore.isDropdownOpen && (
                                    <ul className={styles.DropdownApply}>
                                        <li onClick={handleDropdownOptionClick(1)}>{t("labels.apply")}</li>
                                        <li onClick={handleDropdownOptionClick(2)}>
                                            {t("screens.assemblies.agenda.apply_with_text")}
                                        </li>
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                </UI.Col>
            </UI.Row>
            <UI.Row>
                <UI.Col className={styles.ColContent}>
                    {assemblyStore.formattedRealestates?.length > 0 && (
                        <UI.Row className={"mt-1"}>
                            <UI.Form.Check
                                key={"AGENDAITEMS_LAST_ASSEMBLY"}
                                id={t(
                                    "screens.realestate.assembly.copy_agendaitem_modal.agendaitems_from_last_assembly"
                                ).toString()}
                                className={`${styles.RadioButton} d-flex align-items-center`}
                                label={t(
                                    "screens.realestate.assembly.copy_agendaitem_modal.agendaitems_from_last_assembly"
                                )}
                                type={"radio"}
                                checked={assemblyStore.selectedCopyAssemblyAgendaOption === 1}
                                value={"AGENDAITEMS_LAST_ASSEMBLY"}
                                onChange={onRadioOptionChange}
                            />
                            <UI.Dropdown
                                id="sort-realestates-dorpdown"
                                onSelect={onSelectDropdown}
                                items={assemblyStore.formattedRealestates.map((item) => ({
                                    label: item.name,
                                    value: item.id
                                }))}
                                label={agendaitemStore.selectedRealestate?.name || ""}
                                defaultLabel={assemblyStore.formattedRealestates[0].name}
                                className={styles.RealestateDropdown}
                            />
                        </UI.Row>
                    )}

                    {agendaitemTemplatesCounter > 0 && (
                        <UI.Row className={"mt-1"}>
                            <UI.Form.Check
                                key={"AGENDAITEMS_TEMPLATE_ASSEMBLY"}
                                id={t(
                                    "screens.realestate.assembly.copy_agendaitem_modal.agendaitems_from_template"
                                ).toString()}
                                className={`${styles.RadioButton} d-flex align-items-center`}
                                label={t("screens.realestate.assembly.copy_agendaitem_modal.agendaitems_from_template")}
                                type={"radio"}
                                checked={assemblyStore.selectedCopyAssemblyAgendaOption === 2}
                                value={"AGENDAITEMS_TEMPLATE_ASSEMBLY"}
                                onChange={onRadioOptionChange}
                            />
                        </UI.Row>
                    )}
                </UI.Col>
                <UI.Card
                    title={t("screens.realestate.assembly.copy_agendaitem_modal.preview").toString()}
                    className={styles.PreviewCard}
                >
                    {agendaitemStore.currentAgendaItemToCopy?.length > 0 &&
                        assemblyStore.selectedCopyAssemblyAgendaOption === 1 && (
                            <UI.Row>
                                <ul className={styles.AgendaList}>
                                    {agendaitemStore.currentAgendaItemToCopy.map((parentitem) => (
                                        <li key={parentitem.sort + parentitem.id}>
                                            <div className={styles.ParentAgendaItems}>
                                                {`${parentitem.sort}. ${parentitem.text}`}
                                            </div>
                                            {parentitem.subagendaitemtext.map((childItem) => (
                                                <div
                                                    className={styles.ChildAgendaItems}
                                                    key={childItem.sort + childItem.id}
                                                >
                                                    {`${parentitem.sort}.${childItem.sort} ${childItem.text}`}
                                                </div>
                                            ))}
                                        </li>
                                    ))}
                                </ul>
                            </UI.Row>
                        )}

                    {agendaitemTemplatesCounter > 0 && assemblyStore.selectedCopyAssemblyAgendaOption === 2 && (
                        <UI.Row>
                            <ul className={styles.AgendaList}>
                                {agendaItemTemplates?.map((parentitem) => (
                                    <li key={parentitem.sort + parentitem.id}>
                                        <div className={styles.ParentAgendaItems}>
                                            {`${parentitem.sort}. ${parentitem.text}`}
                                        </div>
                                        {parentitem.subagendaitemtemplatetext.map((childItem) => (
                                            <div
                                                className={styles.ChildAgendaItems}
                                                key={childItem.sort + childItem.id}
                                            >
                                                {`${parentitem.sort}.${childItem.sort} ${childItem.text}`}
                                            </div>
                                        ))}
                                    </li>
                                ))}
                            </ul>
                        </UI.Row>
                    )}
                </UI.Card>
            </UI.Row>
        </UI.Container>
    );
};

export const CopyAgendaItemPreview = Sentry.withProfiler(observer(CopyAgendaItemPreviewBase));
